import { isResource } from '@medplum/core'
import type {
  Appointment,
  Encounter,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { endOfDay, startOfDay } from 'date-fns'
import type { ExportFields } from '#src/ExtendedResource.ts'
import {
  ExtendedEncounter,
  type ExtendedEncounterArgs,
} from '#src/models/encounters/encounter.ts'
import { resolveId } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class AppointmentEncounter extends ExtendedEncounter {
  constructor({
    appointment,
    status,
    ...args
  }: Omit<ExtendedEncounterArgs, 'class' | 'appointment' | 'status'> & {
    appointment: Reference<Appointment>
    status?: Encounter['status']
  }) {
    super({
      ...args,
      class: 'APPOINTMENT',
      status: status ?? 'planned',
    })
    this.appointment = appointment
  }

  get appointment(): Reference<Appointment> | undefined {
    return this.resource.appointment?.[0]
  }

  set appointment(value: Reference<Appointment>) {
    this.resource.appointment ??= []
    this.resource.appointment.push(value)
  }

  protected getExportFields(): ExportFields {
    const appointment = this.resource.appointment?.[0]
    return {
      ...super.getExportFields(),
      appointmentId: appointment ? resolveId(appointment) : undefined,
    }
  }

  static readonly BASE_SEARCH_QUERY: QueryTypes = {
    ...ExtendedEncounter.BASE_QUERY_STRUCTURE,
    class: ExtendedEncounter.ENCOUNTER_CLASS.APPOINTMENT,
  }

  static searchByPatient(patient: Reference<Patient>): QueryTypes {
    return {
      ...this.BASE_SEARCH_QUERY,
      subject: patient,
    }
  }

  static byPatientInProgress(patient: Reference<Patient>): QueryTypes {
    return {
      ...this.BASE_SEARCH_QUERY,
      subject: patient,
      status: 'in-progress',
    }
  }

  static byPractitioner(practitioner: Reference<Practitioner>): QueryTypes {
    return {
      ...this.BASE_SEARCH_QUERY,
      participant: practitioner,
    }
  }

  static byPractitionerInProgress(
    practitioner: Reference<Practitioner>
  ): QueryTypes {
    return {
      ...this.byPractitioner(practitioner),
      status: 'in-progress',
    }
  }

  static byPractitionerInProgressAndDate(
    practitioner: Reference<Practitioner>,
    date: string
  ): QueryTypes {
    const start = startOfDay(date).toISOString()
    const end = endOfDay(date).toISOString()

    return {
      ...this.byPractitionerInProgress(practitioner),
      _filter: `date gt ${start} and date lt ${end}`,
    }
  }

  static byPractitionerAndDate(
    practitioner: Reference<Practitioner>,
    date: string
  ): QueryTypes {
    const start = startOfDay(date).toISOString()
    const end = endOfDay(date).toISOString()

    return {
      ...this.byPractitioner(practitioner),
      _filter: `date gt ${start} and date lt ${end}`,
    }
  }

  static searchByAppointment(appointment: Reference<Appointment>): QueryTypes {
    return {
      ...this.BASE_SEARCH_QUERY,
      appointment: appointment.reference,
    }
  }

  static isInstance(value: unknown): value is Encounter {
    return (
      isResource(value) &&
      isOfType(value, 'Encounter') &&
      value.class.code === ExtendedEncounter.ENCOUNTER_CLASS.APPOINTMENT.code
    )
  }
}
